import React, { useEffect, useMemo, useState } from "react"
import { CouponListData, CouponListTable } from "./CouponListTable"
import { Box, Button, CopyButton, Group, Stack, Tabs, Text, TextInput, ThemeIcon, Title } from "@mantine/core"
import QRCode from "react-qr-code"
import Client from "../../models/client"
import { getCVSummary, getTestReportsSummary, readCookie } from "../api"
import { CheckCircleIcon, DownloadSimpleIcon } from "@phosphor-icons/react"
import { DataTable } from "mantine-datatable"

type ApiSummaryColumn = {
  Header: string
  accessor: string
  className?: string
}
type SummaryColumn = {
  headerName: string
  field: string
  className?: string
}
type SummaryData = {
  columns: SummaryColumn[]
  rows: Record<string, string | number | null>[]
}
const EMPTY_SUMMARY: SummaryData = { columns: [], rows: [] }

type ApiCvColumn = { Header: string; accessor: string; width?: number }
const EMPTY_CV: CouponListData = { columns: [], rows: [] }

const isHanEisu = (str: string) => {
  return /^[A-Za-z0-9&=_-]*$/.test(str)
}

type QrPanelProps = { initialUrl: string }
const QrPanel = ({ initialUrl }: QrPanelProps) => {
  const [url, setUrl] = useState(initialUrl)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetValue = e.target.value
    if (targetValue.startsWith(initialUrl)) {
      const extraText = targetValue.substring(initialUrl.length)
      if (isHanEisu(extraText)) setUrl(initialUrl + extraText)
    } else {
      setUrl(initialUrl)
    }
  }

  return (
    <Box p={32} bdrs={8} style={{ border: '1px solid #0000001A' }}>
      <Group gap={16} align="flex-start" wrap="nowrap">
        <QRCode value={encodeURI(url)} size={120} />
        <Stack style={{ flex: 1, minWidth: 0 }} gap={12}>
          <Group wrap="nowrap">
            <TextInput
              value={url}
              onChange={handleChange}
              style={{ flex: 1 }}
            />
            <CopyButton value={url} timeout={2000}>
              {({ copied, copy }) => (
                <Button
                  onClick={copy}
                  variant="filled"
                  color={copied ? 'gray' : 'invy'}
                  radius="xl"
                  fz={13}
                  fw={600}
                >
                  {copied ? 'コピー済み' : 'コピー'}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Text c="red" fz={13}>
            こちらの検証用QR・URLを公開しないでください。
          </Text>
        </Stack>
      </Group>
    </Box>
  )
}

type Props = { client: Client }
const CVTest = ({ client }: Props) => {
  const clientUrl = client.url_name
  const clientId = client.id

  const inviteUrl =
    window.location.origin + '/' + clientUrl + '/invite?rid=test&'
  const invite2Url =
    window.location.origin + '/' + clientUrl + '/invite2?rid=test&'
  const hasInvite2 = String(client.page_creation) === '1'

  const [tab, setTab] = useState<'invite1' | 'invite2'>('invite1')
  const [reportsSummary, setReportsSummary] =
    useState<SummaryData>(EMPTY_SUMMARY)
  const [cvReport, setCvReport] = useState<CouponListData>(EMPTY_CV)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadCount, setReloadCount] = useState(0)

  useEffect(() => {
    setLoadError(false)
    const p1 = getTestReportsSummary(clientId).then(
      (data: {
        data_columns: ApiSummaryColumn[]
        sum: Record<string, string | number | null>[]
      }) => {
        setReportsSummary({
          columns: data.data_columns.map((c) => ({
            headerName: c.Header,
            field: c.accessor,
            className: c.className,
          })),
          rows: data.sum,
        })
      },
    )
    const p2 = getCVSummary(clientId, 'month', false, true).then(
      (data: {
        columns: ApiCvColumn[]
        cv_lists: Record<string, string | number | null>[]
      }) => {
        setCvReport({
          columns: data.columns.map((c) => ({
            headerName: c.Header,
            field: c.accessor,
            width: c.width,
          })),
          rows: data.cv_lists,
        })
      },
    )

    Promise.all([p1, p2])
      .catch(() => {
        setLoadError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [reloadCount])

  const handleReloadData = () => {
    setLoading(true)
    setReloadCount((c) => c + 1)
  }

  const handleDownloadCsv = () => {
    const token = readCookie('refresh_token')
    const base = `/admin/api/clients/${clientId}/cv-list.csv?date=month&duplicate=false&is_test=true`
    window.location.href = token ? `${base}&ref=${btoa(token)}` : base
  }

  const summaryTableColumns = useMemo(
    () =>
      reportsSummary.columns.map((col) => ({
        accessor: col.field,
        title: col.headerName,
        cellsStyle: (): React.CSSProperties => ({
          textAlign: col.className === 'right' ? 'right' : undefined,
        }),
      })),
    [reportsSummary.columns],
  )

  return (
    <Box>
      <Group gap={12} mb={24}>
        <ThemeIcon variant="transparent" color="#5DA327">
          <CheckCircleIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">
          テスト検証
        </Title>
      </Group>

      <Text fz={13} c="#000000CC" mb={16}>
        テスト検証では、公開中のページが正しく機能し各種データが正しく取得できるかを検証することができます。<br />
        ※検証したいページは、「ページ一覧」メニューより公開中のステータスに変更のうえ検証を開始してください。<br />
        ※必要に合わせて下記URL末尾にパラメータを追加して検証することが可能です。<br />
        ※invyの紹介ページは基本的にスマホでの利用を想定しております。
      </Text>
      <Text fz={13} c="#000000CC" mb={24}>
        検証の際は、下記QRコードをお持ちのスマホで読み取りのうえ、検証を開始してください。<br />
        ※invyの紹介ページは、同一人物の紹介を防止するために、紹介者ページとゲストページは異なったデバイスでないと表示されない仕様になっています。そのため、ゲストページを確認する際は、紹介者ページを開いたデバイスと異なったデバイスで確認する必要があります。
      </Text>

      <Tabs
        value={tab}
        onChange={(val) => val && setTab(val as 'invite1' | 'invite2')}
        mt={40}
      >
        <Tabs.List>
          <Tabs.Tab
            value="invite1"
            style={{ color: tab === 'invite1' ? 'var(--mantine-color-invy-6)' : undefined }}
          >
            inviteページ
          </Tabs.Tab>
          {hasInvite2 && (
            <Tabs.Tab
              value="invite2"
              style={{ color: tab === 'invite2' ? 'var(--mantine-color-invy-6)' : undefined }}
            >
              invite2ページ
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="invite1" py={32}>
          <Title order={2} size={18} fw={600} mb={16}>検証用URL</Title>
          <QrPanel initialUrl={inviteUrl} />
        </Tabs.Panel>

        {hasInvite2 && (
          <Tabs.Panel value="invite2" py={32}>
            <Title order={2} size={18} fw={600} mb={16}>検証用URL</Title>
            <QrPanel initialUrl={invite2Url} />
          </Tabs.Panel>
        )}
      </Tabs>

      <Group justify="space-between" align="flex-end" mt={60} mb={16}>
        <Text fz={13} c="#00000099">
          直近1ヶ月のテストデータ（rid = test）を表示しています。
        </Text>
        <Button
          variant="filled"
          radius="xl"
          fz={13}
          fw={600}
          onClick={handleReloadData}
        >
          更新
        </Button>
      </Group>

      {loadError && <Text c="red" mb={8}>読み込みに失敗しました</Text>}

      <Box
        style={{
          border: '1px solid #0000001A',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <DataTable
          withColumnBorders
          columns={summaryTableColumns}
          records={reportsSummary.rows}
          fetching={loading}
          noRecordsText="データがありません"
          styles={{
            root: { border: 'none' },
            header: { backgroundColor: '#00000008' },
          }}
        />
      </Box>

      <CouponListTable data={cvReport} fetching={loading} />

      <Group mt={16} justify="end">
        <Button
          variant="outline"
          radius="xl"
          leftSection={<DownloadSimpleIcon size={16} weight="bold" />}
          onClick={handleDownloadCsv}
        >
          CSVダウンロード
        </Button>
      </Group>
    </Box>
  )
}

export default CVTest
